import { Check } from "@mui/icons-material";
import { Alert, Card, CircularProgress, Stack, Typography } from "@mui/material";
import {
  type Company,
  type CompanySurvey,
  type CompanyTag,
  type Currency,
  type FundingRound,
  type Headcount,
  type Image as ImageModel,
  type MarketDataSearchSource,
} from "@prisma/client";
import NextImage from "next/image";
import { useRouter } from "next/router";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useRendersCount } from "react-use";
import equitySurveySvg from "~/assets/illustrations/equity-survey.svg";
import { value } from "~/components/helpers";
import { MarketDataChart } from "~/components/market-data/MarketDataChart";
import { MarketDataFilters } from "~/components/market-data/MarketDataFilters";
import { MarketDataForm } from "~/components/market-data/MarketDataForm";
import { MarketDataTable } from "~/components/market-data/MarketDataTable";
import { ContactButton } from "~/components/ui/ContactButton";
import { Icon } from "~/components/ui/core/Icon";
import { type NumberRange } from "~/components/ui/LegacyCompanyFilters";
import { useScreenDimensions } from "~/hooks/useScreenDimensions";
import { useSession } from "~/hooks/useSession";
import { trackMarketDataSearched } from "~/lib/external/segment/web/events";
import { clamp, first, isEmpty, isEqual, last, orderBy } from "~/lib/lodash";
import { EmployeeLevel, formatLevel, getLevelMetaData } from "~/services/employee/employeeLevel";
import { type PercentileBounds } from "~/services/percentiles";

import classNames from "classnames";
import { Trans } from "react-i18next";
import { HeaderLayout } from "~/components/layout/HeaderLayout";
import { MarketDataShareModal } from "~/components/market-data/MarketDataShareModal";
import { Button } from "~/components/ui/core/Button";
import { CurrencySelector } from "~/components/ui/CurrencySelector";
import { type LocationForSelector } from "~/components/ui/LocationSelector";
import { SelectDisplayEmployees } from "~/components/ui/market-data-browser/SelectDisplayEmployees";
import { PercentilesSelector } from "~/components/ui/PercentilesSelector";
import { useClickOutside } from "~/hooks/useClickOutside";
import { useFeatureFlags } from "~/hooks/useFeatureFlags";
import { usePermissions } from "~/hooks/usePermissions";
import { useI18n } from "~/lib/i18n/useI18n";
import { getId } from "~/lib/utils";
import {
  type GetMarketDataStatsMeasure,
  type GetMarketDataStatsResponse,
  GetMarketDataStatsSchema,
  type Input as PayloadInput,
  useGetMarketDataStatsQuery,
} from "~/pages/api/get-market-data-stats";
import { useSaveRecentMarketDataSearchMutation } from "~/pages/api/market-data-search/save-recent-market-data-search";
import {
  GetMarketDataDatasetEmployeesStatsSchema,
  useGetMarketDataDatasetEmployeesStatsQuery,
} from "~/pages/api/market-data/get-market-data-dataset-employees-stats";
import { type Measure } from "~/services/employee";
import { type EmployeeStatsMeasure } from "~/services/employee-stats/utils";
import { type ConvertFiltersPresetsToInputResult } from "~/services/filters-preset/convertFiltersPresetsToInput";
import { buildMarketDataQuery } from "~/services/market-data/market-data-filters/buildMarketDataQuery";
import { DEFAULT_MARKET_DATA_MEASURE } from "~/services/market-data/market-data-filters/parseQueryString";
import { MarketDataStatsSource } from "~/services/marketCompensationStats";

type CompanyRow = Company & {
  logo: ImageModel | null;
  cover: ImageModel | null;
  liveSurvey: CompanySurvey | null;
};

type Props = {
  company?: CompanyRow;
  initialInput: Input;
  initialStats: GetMarketDataStatsResponse | null;
  defaultLocations: LocationForSelector[];
  useTestData?: boolean;
  filtersPresets: ConvertFiltersPresetsToInputResult;
  readOnly?: boolean;
  title?: string;
  onReadOnlyInstructionsClick?: () => void;
};

export type MarketOrCompareInput = {
  fundingRounds: FundingRound[];
  headcount: Headcount | null;
  industries: CompanyTag[];
  industry: CompanyTag | null;
  jobIds: string[];
  labels: CompanyTag[];
  locations: LocationForSelector[];
  size: NumberRange;
};

export type Input = {
  measure: Measure;
  currency: Currency;
  levels: EmployeeLevel[];
  benchmarkLevelIds: number[];
  mergeAdvancedLevels: boolean;
  useBenchmarkLevelsInShare: boolean;
  market: MarketOrCompareInput;
  showCompanyEmployees: boolean;
  compare: MarketOrCompareInput & {
    enabled: boolean;
  };
  percentiles: PercentileBounds;
  presetId?: number;
};

export const mapMarketDataInput = (input: Input): Omit<PayloadInput, "useTestData"> => {
  return {
    presetId: input.presetId,
    currency: input.currency.code,
    measure: input.measure,
    levels: input.levels,
    useBenchmarkLevelsInShare: input.useBenchmarkLevelsInShare ?? false,
    benchmarkLevelIds: input.benchmarkLevelIds,
    mergeAdvancedLevels: input.mergeAdvancedLevels,
    showCompanyEmployees: input.showCompanyEmployees,
    market: {
      fundingRounds: input.market.fundingRounds,
      headcount: input.market.headcount,
      industriesIds: input.market.industries.map(getId),
      industryId: input.market.industry?.id ?? null,
      jobsIds: input.market.jobIds,
      labelsIds: input.market.labels.map(getId),
      locationsIds: input.market.locations.map(getId),
      size: input.market.size,
    },
    compare: input.compare.enabled
      ? {
          fundingRounds: input.compare.fundingRounds,
          headcount: input.compare.headcount,
          industriesIds: input.compare.industries.map(getId),
          industryId: input.compare.industry?.id ?? null,
          jobsIds: input.compare.jobIds,
          labelsIds: input.compare.labels.map(getId),
          locationsIds: input.compare.locations.map(getId),
          size: input.compare.size,
        }
      : null,
    percentiles: input.percentiles,
  };
};

export const MarketDataBrowser: React.FC<Props> = ({
  company,
  initialInput,
  initialStats,
  defaultLocations,
  filtersPresets,
  useTestData = false,
  readOnly = false,
  onReadOnlyInstructionsClick,
  title,
}) => {
  const { t, formatPercentile } = useI18n();
  const { CAN_ACCESS_FIGURES_AI_V2, CAN_ACCESS_LEVEL_FRAMEWORKS, CAN_ACCESS_BENCHMARK_V2 } = useFeatureFlags();
  const { permissions } = usePermissions();
  const router = useRouter();
  const { user } = useSession();
  const screenDimensions = useScreenDimensions();
  const chart = useRef<HTMLDivElement>(null);
  const hasRendered = useRendersCount() > 1;

  const saveRecentMarketDataSearchMutation = useSaveRecentMarketDataSearchMutation();

  const canAccessBenchmarkLevels = CAN_ACCESS_LEVEL_FRAMEWORKS || initialInput.useBenchmarkLevelsInShare;

  const [hasDoneInitialSearch, setHasDoneInitialSearch] = useState(initialStats !== null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [chartHeight, setChartHeight] = useState(500);
  const [marketDataMeasure, setMarketDataMeasure] = useState<Measure>(
    initialInput.measure ?? DEFAULT_MARKET_DATA_MEASURE
  );

  const [shouldFetch, setShouldFetch] = useState<boolean>(true);
  const [input, setInput] = useState<Input>(initialInput);

  const [showPercentilesMenu, setShowPercentilesMenu] = useState(false);
  const percentilesMenuRef = useClickOutside<HTMLDivElement>(() => {
    setShowPercentilesMenu(false);
  });
  const [isCollapsed, setIsCollapsed] = useState(user?.flags.collapseMarketDataFilters ?? false);

  const { data: originalStats, isFetching: isFetchingOriginalStats } = useGetMarketDataStatsQuery({
    input: { useTestData, ...mapMarketDataInput(input) },
    options: {
      initialData: initialStats,
      enabled:
        !CAN_ACCESS_BENCHMARK_V2 &&
        hasDoneInitialSearch &&
        GetMarketDataStatsSchema.isValidSync({ useTestData, ...mapMarketDataInput(input) }),
    },
  });

  const { data: datasetEmployeesStats, isFetching: isFetchingDatasetEmployeesStats } =
    useGetMarketDataDatasetEmployeesStatsQuery({
      input: { useTestData, ...mapMarketDataInput(input) },
      options: {
        initialData: initialStats,
        enabled:
          CAN_ACCESS_BENCHMARK_V2 &&
          hasDoneInitialSearch &&
          GetMarketDataDatasetEmployeesStatsSchema.isValidSync({ useTestData, ...mapMarketDataInput(input) }),
      },
    });

  const data = CAN_ACCESS_BENCHMARK_V2 ? datasetEmployeesStats : originalStats;

  const isFetching = CAN_ACCESS_BENCHMARK_V2 ? isFetchingDatasetEmployeesStats : isFetchingOriginalStats;

  const onMeasureChange = (value: GetMarketDataStatsMeasure) => {
    setMarketDataMeasure(value as EmployeeStatsMeasure);
    mergeInput({
      measure: value as EmployeeStatsMeasure,
    });
  };

  const onMergeInput = (newInput: Partial<Input>) => {
    const keys = Object.keys(newInput);
    if (keys.length === 1 && keys[0] === "measure") {
      setShouldFetch(false);
      return;
    }

    setShouldFetch(true);
  };

  const mergeInput = (newInput: Partial<Input>) => {
    onMergeInput(newInput);

    //This allows to reset the preset input when something else is changed -- to be monitored
    if (!newInput.presetId) {
      newInput.presetId = undefined;
    }

    setInput({ ...input, ...newInput });
  };

  const updateCurrency = (currency: Currency) => {
    if (currency.code === input.currency.code) {
      return;
    }

    mergeInput({
      currency,
    });
  };

  const onClearFiltersClick = () => {
    mergeInput({
      market: {
        ...input.market,
        fundingRounds: [],
        headcount: null,
        industries: [],
        industry: null,
        labels: [],
        locations: defaultLocations,
        size: { min: null, max: null },
      },
      compare: {
        ...input.compare,
        fundingRounds: [],
        headcount: null,
        industries: [],
        industry: null,
        labels: [],
        locations: defaultLocations,
        size: { min: null, max: null },
      },
    });
  };

  const onSelectRelevantLevelsClick = () => {
    const orderedLevels = Object.values(EmployeeLevel);

    const firstLevelIndex = orderedLevels.indexOf(first(levelsWithResults) as EmployeeLevel);
    const lastLevelIndex = orderedLevels.indexOf(last(levelsWithResults) as EmployeeLevel);
    const levelsRange = Object.values(EmployeeLevel).filter((level, index) => {
      return !getLevelMetaData(level).advanced && index >= firstLevelIndex && index <= lastLevelIndex;
    });

    mergeInput({
      levels: levelsRange,
    });
  };

  const onMarketDataFormSubmit = async () => {
    setHasDoneInitialSearch(true);

    const body = {
      ...mapMarketDataInput(input),
      useTestData,
    };

    await trackMarketDataSearched({ input: body, origin: "INITIAL_SEARCH" });

    if (!useTestData) {
      await saveRecentMarketDataSearchMutation.mutateAsync({ search: body });
    }

    if (!chart.current) return;
    setChartHeight(clamp(screenDimensions.height - chart.current.getBoundingClientRect().y - 56, 450, 1000));
  };

  useEffect(() => {
    setInput(initialInput);
    setHasDoneInitialSearch(initialStats !== null);
    setMarketDataMeasure(initialInput.measure ?? DEFAULT_MARKET_DATA_MEASURE);
  }, [initialStats, initialInput]);

  useEffect(() => {
    if (router.query.origin && initialStats) {
      void trackMarketDataSearched({
        input: {
          ...mapMarketDataInput(initialInput),
          useTestData,
        },
        origin: router.query.origin as MarketDataSearchSource,
      });
    }
  }, []);

  useEffect(() => {
    if (!hasRendered) {
      return;
    }

    if (input.compare.enabled) {
      mergeInput({
        compare: {
          ...input.market,
          ...input.compare,
        },
      });
    } else {
      mergeInput({
        compare: {
          ...input.compare,
          fundingRounds: [],
          headcount: null,
          industries: [],
          jobIds: [],
          labels: [],
          locations: defaultLocations,
          size: { min: null, max: null },
        },
      });
    }
  }, [input.showCompanyEmployees]);

  useEffect(() => {
    if (readOnly || !hasRendered || !shouldFetch) {
      return;
    }

    const query = buildMarketDataQuery(
      { user, input, defaultLocations },
      { canAccessFiguresAiV2: CAN_ACCESS_FIGURES_AI_V2 }
    );
    void router.replace({ pathname: router.route, query }, undefined, { shallow: true });
  }, [input]);

  useEffect(() => {
    if (!chart.current) {
      return;
    }

    setChartHeight(clamp(screenDimensions.height - chart.current.getBoundingClientRect().y - 256, 450, 1000));
  }, [data, screenDimensions.height]);

  const headcountOrSize = CAN_ACCESS_FIGURES_AI_V2
    ? input.market.headcount !== null
    : input.market.size.min !== null || input.market.size.max !== null;
  const hasAdditionalActiveFilter =
    input.market.fundingRounds.length > 0 ||
    headcountOrSize !== null ||
    input.market.industries.length > 0 ||
    input.market.industry !== null ||
    input.market.labels.length > 0;

  // multiple filters implies we can't use Figures AI
  const showIncompleteResultsWarningDueToMultipleFilters = useMemo(() => {
    const hasIncompleteResultsFor = (params: {
      dataByLevel: GetMarketDataStatsResponse["market"]["byLevel"] | undefined;
      levels: EmployeeLevel[];
      input?: MarketOrCompareInput;
    }) => {
      const hasPredictiveResults = Object.values(params.dataByLevel ?? {}).some(
        (level) => level?.source === MarketDataStatsSource.PREDICTIVE
      );

      const hasMultipleFiltersIncompatibleWithFiguresAI = params.input && params.input.industries.length > 1;

      const hasNoResultsForSomeLevel = params.levels.some(
        (level) => params.dataByLevel && params.dataByLevel[level] === null
      );

      return !hasPredictiveResults && hasMultipleFiltersIncompatibleWithFiguresAI && hasNoResultsForSomeLevel;
    };

    return (
      hasIncompleteResultsFor({ dataByLevel: data?.market.byLevel, levels: input.levels, input: input.market }) ||
      hasIncompleteResultsFor({ dataByLevel: data?.comparison?.byLevel, levels: input.levels, input: input.compare })
    );
  }, [data, input]);

  const hasPredictiveResults = useMemo(() => {
    const marketRows = Object.values(data?.market.byLevel ?? {});
    const comparisonRows = Object.values(data?.comparison?.byLevel ?? {});

    return [...marketRows, ...comparisonRows].some((level) => level?.source === MarketDataStatsSource.PREDICTIVE);
  }, [data]);

  const showPredictiveResultsWithFiltersWarning = input.market.labels.length > 0 && hasPredictiveResults;

  if (!hasDoneInitialSearch) {
    return (
      <MarketDataForm
        useTestData={useTestData}
        filtersPresets={filtersPresets}
        onChange={(newInput) => {
          mergeInput({
            presetId: newInput.presetId,
            levels: newInput.levels,
            benchmarkLevelIds: newInput.benchmarkLevelIds,
            market: {
              ...input.market,
              jobIds: newInput.jobIds,
              locations: newInput.locations,
            },
          });
        }}
        onSubmit={onMarketDataFormSubmit}
      />
    );
  }

  const levelsWithResults = Object.values(EmployeeLevel).filter((level) => {
    if (!data) {
      return false;
    }

    const hasMarketResults = !!data.market.byLevel[level];
    const hasCompanyResults = !isEmpty(data.market.byLevel[level]?.companyEmployees);
    const hasComparisonResults = !!data.comparison?.byLevel[level];
    const shouldCompareWithJobs = !!data.comparison && input.compare.jobIds.length > 0;

    return (
      hasMarketResults ||
      (input.showCompanyEmployees && hasCompanyResults) ||
      (shouldCompareWithJobs && hasComparisonResults)
    );
  });

  const showEmptyResultsIndication = value(() => {
    if (!data || !hasDoneInitialSearch) {
      return false;
    }

    const hasResults = !canAccessBenchmarkLevels
      ? input.levels.some((level) => {
          return levelsWithResults.includes(level);
        })
      : true;

    const hasActiveFilter =
      !isEqual(orderBy(input.market.locations.map(getId)), orderBy(defaultLocations.map(getId))) ||
      hasAdditionalActiveFilter;

    return !hasResults && hasActiveFilter;
  });

  const showEmptyResultsForLevelIndication =
    !showEmptyResultsIndication &&
    value(() => {
      if (!data || !hasDoneInitialSearch) {
        return false;
      }

      const hasResultsForLevel = !canAccessBenchmarkLevels
        ? input.levels.some((level) => levelsWithResults.includes(level))
        : true;

      const hasResultsForOtherLevels = Object.values(EmployeeLevel).some((level) => {
        const hasMarketResults = !!data.market.byLevel[level];
        const hasCompanyResults = !isEmpty(data.market.byLevel[level]?.companyEmployees);
        const hasComparisonResults = !!data.comparison?.byLevel[level];
        const shouldCompareWithJobs = input.compare.enabled && input.compare.jobIds.length > 0;

        return (
          hasMarketResults &&
          (!input.showCompanyEmployees || hasCompanyResults) &&
          (!shouldCompareWithJobs || hasComparisonResults)
        );
      });

      return !hasResultsForLevel && hasResultsForOtherLevels;
    });

  const testDataBannerHeight = 24;

  const props = data
    ? {
        useTestData,
        useBenchmarkLevelsInShare: input.useBenchmarkLevelsInShare ?? false,
        onMeasureChange,
        marketDataMeasure,
        defaultLocations,
        categories: input.levels,
        benchmarkLevelIds: input.benchmarkLevelIds,
        comparison: data?.comparison
          ? {
              byLevel: data.comparison.byLevel,
              locations: input.compare.locations,
              jobIds: input.compare.jobIds,
            }
          : undefined,
        currency: data?.currency,
        height: chartHeight - (useTestData ? testDataBannerHeight : 0),
        isLoading: isFetching,
        input,
        market: {
          byLevel: data?.market.byLevel,
          locations: input.market.locations,
          jobIds: input.market.jobIds,
        },
        percentiles: data?.percentiles,
      }
    : undefined;

  return (
    <HeaderLayout
      childrenClassName="w-full relative flex"
      title={title ?? t("pages.market-data.market-data-browser.title")}
      extraActions={
        <Stack direction="row" spacing={4} alignItems="center">
          {permissions.canAccessYouVsMarket && (
            <SelectDisplayEmployees
              displayEmployees={input.showCompanyEmployees}
              onChange={(event) => {
                mergeInput({
                  showCompanyEmployees: event.target.value,
                });
              }}
            />
          )}
          {data?.suggestedCurrencies && data?.suggestedCurrencies.length > 1 && (
            <CurrencySelector
              currencies={data?.suggestedCurrencies}
              value={input.currency}
              disabled={readOnly}
              onChange={(event) => updateCurrency(event.target.value)}
              formatLabel={(currency) => {
                return (
                  <div className="group ml-6 mt-1 flex items-center">
                    <span className="flex items-center text-sm">
                      <Icon
                        className="transform text-gray-500 transition group-hover:scale-110 group-hover:text-primary-600"
                        name="currency"
                      />
                      <span className="ml-1.5">{currency?.name}</span>
                      <span className="ml-1">({currency?.symbol})</span>
                    </span>

                    <Icon className="text-gray-500" name="menu-down" />
                  </div>
                );
              }}
            />
          )}

          <Stack>
            <Button
              color="inherit"
              className="font-normal hover:bg-transparent"
              disabled={readOnly}
              onClick={() => {
                setShowPercentilesMenu(!showPercentilesMenu);
              }}
              startIcon={<Icon name="percentile" />}
              endIcon={<Icon name="menu-down" />}
            >
              {t("pages.market-data.market-data-browser.target-percentile", {
                percentile: formatPercentile(input.percentiles.middle),
              })}
            </Button>

            {showPercentilesMenu && (
              <div
                ref={percentilesMenuRef}
                className="absolute right-0 top-0 z-50 mr-1 mt-7 flex w-[400px] flex-col items-start rounded border border-gray-900 bg-white px-2 py-2 pb-0 text-sm text-gray-900 shadow-2xl outline-none"
              >
                <PercentilesSelector
                  className="w-full"
                  readOnly={readOnly}
                  value={input.percentiles}
                  onChange={(event) => {
                    mergeInput({
                      percentiles: event.target.value,
                    });
                  }}
                />
              </div>
            )}
          </Stack>

          {!readOnly && user && !useTestData && (
            <div className="relative hidden sm:block">
              <MarketDataShareModal
                input={input}
                isOpen={showShareModal}
                onClose={() => {
                  return setShowShareModal(false);
                }}
              />

              <Button disableElevation onClick={() => setShowShareModal(true)} variant="contained">
                {t("pages.market-data.market-data-browser.share")}
              </Button>
            </div>
          )}
        </Stack>
      }
      breadcrumbs={[
        {
          href: { pathname: "/home" },
          text: t("pages.market-data.market-data-browser.home"),
        },
        {
          text: t("pages.market-data.market-data-browser.market-data"),
        },
      ]}
      withSpacing={false}
    >
      <MarketDataFilters
        company={company}
        input={input}
        filtersPresets={filtersPresets}
        readOnly={readOnly}
        useTestData={useTestData}
        onChange={mergeInput}
        onReadOnlyInstructionsClick={() => onReadOnlyInstructionsClick?.()}
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
      />

      <div
        className={classNames("top-4 flex h-full w-full flex-auto flex-col space-y-4 px-8", {
          "ml-64 w-[calc(100%-16rem)]": !isCollapsed,
        })}
        style={{ height: `calc(${chartHeight}px + 2.5rem)`, minHeight: `calc(${chartHeight}px + 2.5rem)` }}
      >
        {isFetching && <CircularProgress className="self-align absolute top-10 self-center" />}

        <div className="relative flex-1">
          {props && (
            <div ref={chart} className="flex-1 space-y-6 pb-10 pl-4 pt-2">
              {useTestData && (
                <Card variant="outlined" className="flex-1">
                  <Stack direction="row" justifyContent="space-between">
                    <Stack spacing={2}>
                      <Stack direction="row" alignItems="center" spacing={4}>
                        <Typography variant="h2">
                          {t("pages.market-data.market-data-browser.want-to-see-more")}
                        </Typography>
                        <ContactButton className="ml-4" source="figures-app-try-mode" />
                      </Stack>

                      <Stack>
                        <Typography>
                          <Check fontSize="small" /> {t("pages.market-data.market-data-browser.compare-in-seconds")}
                        </Typography>
                        <Typography>
                          <Check fontSize="small" />{" "}
                          {t("pages.market-data.market-data-browser.focus-on-areas-of-attention")}
                        </Typography>
                        <Typography>
                          <Check fontSize="small" />{" "}
                          {t("pages.market-data.market-data-browser.access-unique-gender-equality")}
                        </Typography>
                        <Typography>
                          <Check fontSize="small" /> {t("pages.market-data.market-data-browser.save-time")}
                        </Typography>
                      </Stack>
                    </Stack>

                    <div className="hidden lg:block">
                      <img className="h-20 rounded-lg shadow xl:h-32" src="/images/landing/dashboard-preview.png" />
                      <Typography sx={{ fontStyle: "italic" }}>
                        {t("pages.market-data.market-data-browser.compensation-gender-equality-indices")}
                      </Typography>
                    </div>
                  </Stack>
                </Card>
              )}

              {showPredictiveResultsWithFiltersWarning && (
                <Alert severity="warning">
                  <Typography>
                    {t("pages.market-data.market-data-browser.our-ai-estimated-data-is-still-learning")}
                  </Typography>
                </Alert>
              )}

              {showIncompleteResultsWarningDueToMultipleFilters && (
                <Alert severity="warning">
                  <Typography>
                    {t("pages.market-data.market-data-browser.less-results-and-no-ai-because-multiple-filters")}
                  </Typography>
                </Alert>
              )}

              {showEmptyResultsIndication ? (
                <MarketDataBrowserEmptyResults>
                  <Button onClick={onClearFiltersClick} variant="contained" size="small" className="!mx-auto">
                    {t("pages.market-data.market-data-browser.clear-active-filters")}
                  </Button>
                </MarketDataBrowserEmptyResults>
              ) : (
                <Stack spacing={8}>
                  <MarketDataChart {...props} />
                  <MarketDataTable {...props} />
                </Stack>
              )}
            </div>
          )}

          {(showEmptyResultsForLevelIndication || (!data && !isFetching)) && (
            <div className="absolute inset-x-0 bottom-0 top-0 z-30 flex items-center justify-center bg-gray-900 bg-opacity-75 py-4">
              <Card className="pointer-events-auto border-l-4 border-secondary-400">
                {showEmptyResultsForLevelIndication && (
                  <Stack spacing={4}>
                    <Typography variant="subtitle2">
                      {t("pages.market-data.market-data-browser.we-could-not-find-any-employees")}
                    </Typography>
                    <Stack>
                      <Typography>
                        {t("pages.market-data.market-data-browser.following-levels-have-employees")}
                      </Typography>
                      {levelsWithResults.map((level) => {
                        return (
                          <Typography key={level}>
                            · {formatLevel(t, level, company?.useAdvancedLevels ?? true)}
                          </Typography>
                        );
                      })}
                    </Stack>

                    <Button variant="contained" size="small" onClick={onSelectRelevantLevelsClick}>
                      {t("pages.market-data.market-data-browser.select-levels-with-results")}
                    </Button>
                  </Stack>
                )}

                {!data && (
                  <Stack>
                    <Typography variant="subtitle2">
                      {t("pages.market-data.market-data-browser.please-select-at-least-one-job")}
                    </Typography>
                  </Stack>
                )}
              </Card>
            </div>
          )}
        </div>
      </div>
    </HeaderLayout>
  );
};

const MarketDataBrowserEmptyResults: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { t } = useI18n();

  return (
    <Stack spacing={4} className="m-auto mt-10 max-w-xl" justifyContent="center">
      <Stack spacing={3}>
        <Stack alignItems="center">
          <NextImage
            alt="dog looking through magnifying glass"
            src={equitySurveySvg}
            width={103}
            height={113}
            unoptimized
          />
        </Stack>

        <Stack spacing={2}>
          <Typography variant="h2" align="center">
            {t("pages.market-data.market-data-browser.no-market-data-found")}
          </Typography>
          <Stack>
            <Typography align="center" color="text.secondary">
              <Trans i18nKey="pages.market-data.market-data-browser.search-didnt-return-any-market-data">
                <br />
              </Trans>
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      {children}
    </Stack>
  );
};
